import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import cn from 'classnames';
import { Collapsible, LinkButton, Text } from '@el8/vital';
import { getPractice } from 'modules/practice';
import { getScheduleForPractice } from 'modules/schedule';

import { State, ScheduleInfo, ScheduleDayInfo } from 'types';
import { Box } from 'components';
import ChevronIcon from './ChevronIcon';
import styles from './PracticeSchedule.less';

type PracticeScheduleOwnProps = {
  practiceId: number;
};

type PracticeScheduleStateProps = {
  schedule: ScheduleInfo;
};

type PracticeScheduleProps = {} & PracticeScheduleOwnProps & PracticeScheduleStateProps;

function getToday(): string {
  const practiceDateTime = DateTime.local();
  return practiceDateTime.weekdayLong.toLowerCase();
}

function getDayAbbreviation(day: string): string {
  switch (day) {
    case 'monday':
      return 'Mon';
    case 'tuesday':
      return 'Tue';
    case 'wednesday':
      return 'Wed';
    case 'thursday':
      return 'Thur';
    case 'friday':
      return 'Fri';
    case 'saturday':
      return 'Sat';
    case 'sunday':
      return 'Sun';
    default:
      throw new Error(`Unrecognized day: ${day}`);
  }
}

function getExpandButtonText(todayHours: ScheduleDayInfo, isExpanded: boolean): string {
  if (todayHours) {
    return isExpanded
      ? 'Open today'
      : `Open today ${todayHours.startTime} \u2014 ${todayHours.endTime}`;
  }
  return 'Closed today';
}

const PracticeSchedule: React.SFC<PracticeScheduleProps> = ({ schedule }): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleIsExpanded = useCallback(
    () => setIsExpanded((currentIsExpanded) => !currentIsExpanded),
    [setIsExpanded],
  );

  if (!schedule) return null;

  const today = getToday();
  const todayHours = schedule[today] as ScheduleDayInfo;

  return (
    <Box direction="column" align="center" justify="center">
      <span style={{ position: 'relative' }}>
        <LinkButton
          onClick={toggleIsExpanded}
          text={getExpandButtonText(todayHours, isExpanded)}
          style={{ fontSize: 12, fontWeight: 500 }}
        />
        <button type="button" onClick={toggleIsExpanded} className={styles.expandButton}>
          <ChevronIcon className={cn(styles.chevronIcon, isExpanded && styles.flipped)} />
        </button>
      </span>
      <Collapsible isOpen={isExpanded}>
        <div className={styles.schedule}>
          {Object.keys(schedule).map((day) => {
            const hours = schedule[day] as ScheduleDayInfo;

            const isToday = day === today;
            const appearance = isToday ? 'body' : 'system';
            const weight = isToday ? 'medium' : 'regular';

            return (
              <Box align="center" className={styles.row} key={day}>
                <Text appearance={appearance} weight="medium" className={styles.day}>
                  {getDayAbbreviation(day)}
                </Text>
                {hours ? (
                  <React.Fragment>
                    <Text
                      align="right"
                      appearance={appearance}
                      className={styles.startTime}
                      weight={weight}
                    >
                      {hours.startTime}
                    </Text>
                    <Text appearance={appearance} weight={weight}>
                      {'\u2014'}
                    </Text>
                    <Text appearance={appearance} className={styles.endTime} weight={weight}>
                      {hours.endTime}
                    </Text>
                  </React.Fragment>
                ) : (
                  <Text appearance={appearance} align="center" className={styles.closed}>
                    Closed
                  </Text>
                )}
              </Box>
            );
          })}
        </div>
      </Collapsible>
    </Box>
  );
};

const mapStateToProps = (state: State, ownProps: PracticeScheduleOwnProps) => ({
  practice: getPractice(state, ownProps),
  schedule: getScheduleForPractice(state, ownProps),
});

export default connect(mapStateToProps)(PracticeSchedule);
